// @ts-nocheck
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Users, Brain, Plus, Trash2, Eye, AlertCircle, CheckCircle, Sparkles, Target } from 'lucide-react';
import { SEGMENT_FIELDS, OPERATORS, LOGICAL_OPERATORS } from '../../types/segment';
import { calculateSegmentPreview, validateSegmentCriteria } from '../../utils/segmentEvaluation';
import SegmentSaveForm from './SegmentSaveForm';
import { SelectField } from "components/ui/select-field";

const CreateSegmentModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  leads = [], 
  existingSegments = [],
  prefilledCriteria = null,
  editingSegment = null // New prop for editing mode
}) => {
  const [activeTab, setActiveTab] = useState('manual');
  const [segmentName, setSegmentName] = useState('');
  const [segmentDescription, setSegmentDescription] = useState('');
  const [segmentTags, setSegmentTags] = useState('');
  const [criteria, setCriteria] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  
  // AI-assisted tab state
  const [aiDescription, setAiDescription] = useState('');
  const [aiInterpretation, setAiInterpretation] = useState(null);
  const [aiConfidence, setAiConfidence] = useState(0);
  const [isAiProcessing, setIsAiProcessing] = useState(false);

  // Initialize with prefilled criteria if provided
  useEffect(() => {
    if (prefilledCriteria && prefilledCriteria.length > 0) {
      setCriteria(prefilledCriteria);
      setActiveTab('manual');
    }
  }, [prefilledCriteria]);

  // Initialize with existing segment data when editing
  useEffect(() => {
    if (editingSegment) {
      setSegmentName(editingSegment.name || '');
      setSegmentDescription(editingSegment.description || '');
      setSegmentTags(editingSegment.tags ? editingSegment.tags.join(', ') : '');
      setCriteria(editingSegment.criteria || []);
      setActiveTab(editingSegment.aiGenerated ? 'ai' : 'manual');
      
      // If it was AI-generated, try to reconstruct the description
      if (editingSegment.aiGenerated && editingSegment.criteria) {
        const reconstructedDescription = reconstructAiDescription(editingSegment.criteria);
        setAiDescription(reconstructedDescription);
      }
    }
  }, [editingSegment]);

  // Update preview when criteria change
  useEffect(() => {
    if (criteria.length > 0) {
      updatePreview();
    } else {
      setPreviewData(null);
    }
  }, [criteria, leads]);

  const updatePreview = () => {
    setIsPreviewLoading(true);
    try {
      const preview = calculateSegmentPreview(leads, criteria);
      setPreviewData(preview);
    } catch (error) {
      console.error('Error calculating preview:', error);
      setPreviewData(null);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const addCriterion = () => {
    const newCriterion = {
      id: Date.now().toString(),
      field: '',
      operator: 'equals',
      value: '',
      weight: 1,
      logicalOperator: criteria.length > 0 ? 'AND' : undefined
    };
    setCriteria([...criteria, newCriterion]);
  };

  const updateCriterion = (id, updates) => {
    setCriteria(criteria.map(c => 
      c.id === id ? { ...c, ...updates } : c
    ));
  };

  const removeCriterion = (id) => {
    setCriteria(criteria.filter(c => c.id !== id));
  };

  const handleSave = () => {
    // Clear previous validation errors
    setValidationErrors([]);
    
    // Validate form
    const errors = [];
    
    if (!segmentName.trim()) {
      errors.push('Segment name is required.');
    }
    
    // Check for duplicate names, but allow keeping the same name when editing
    if (segmentName.trim() && existingSegments.some(s => 
      s.name.toLowerCase() === segmentName.trim().toLowerCase() && 
      (!editingSegment || s.id !== editingSegment.id)
    )) {
      errors.push('A segment with this name already exists.');
    }
    
    const criteriaValidation = validateSegmentCriteria(criteria);
    if (!criteriaValidation.isValid) {
      errors.push(...criteriaValidation.errors);
    }
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Create or update segment object
    const segment = {
      id: editingSegment ? editingSegment.id : Date.now().toString(),
      name: segmentName.trim(),
      description: segmentDescription.trim(),
      criteria,
      count: previewData?.estimatedSize || 0,
      size: previewData?.estimatedSize || 0, // Keep both for compatibility
      growth: editingSegment ? editingSegment.growth : '+0%', // Preserve existing growth or default for new
      color: editingSegment ? editingSegment.color : 'blue', // Preserve existing color or default
      aiGenerated: activeTab === 'ai',
      tags: segmentTags.split(',').map(tag => tag.trim()).filter(Boolean),
      createdAt: editingSegment ? editingSegment.createdAt : new Date(),
      updatedAt: new Date(),
      createdBy: editingSegment ? editingSegment.createdBy : (activeTab === 'ai' ? 'ai' : 'user'),
      usageCount: editingSegment ? editingSegment.usageCount : 0,
      lastUsed: editingSegment ? editingSegment.lastUsed : null,
      expectedPerformance: editingSegment ? editingSegment.expectedPerformance : {
        openRate: 0.25,
        clickRate: 0.05,
        conversionRate: 0.02
      },
      status: editingSegment ? editingSegment.status : 'active'
    };
    
    onSave(segment);
    handleClose();
  };

  const handleClose = () => {
    // Reset form
    setSegmentName('');
    setSegmentDescription('');
    setSegmentTags('');
    setCriteria([]);
    setPreviewData(null);
    setValidationErrors([]);
    setAiDescription('');
    setAiInterpretation(null);
    setAiConfidence(0);
    onClose();
  };

  const processAiDescription = async () => {
    if (!aiDescription.trim()) return;
    
    setIsAiProcessing(true);
    
    // Simulate AI processing (replace with actual AI service call)
    setTimeout(() => {
      const mockInterpretation = generateMockAiInterpretation(aiDescription);
      setAiInterpretation(mockInterpretation);
      setCriteria(mockInterpretation.criteria);
      setAiConfidence(mockInterpretation.confidence);
      setIsAiProcessing(false);
    }, 2000);
  };

  // Helper function to reconstruct AI description from criteria (for editing AI-generated segments)
  const reconstructAiDescription = (criteriaList) => {
    if (!criteriaList || criteriaList.length === 0) return '';
    
    const parts = [];
    criteriaList.forEach(criterion => {
      if (criterion.field === 'status') {
        parts.push(`${criterion.value} leads`);
      } else if (criterion.field === 'leadScore') {
        parts.push(`leads with scores ${criterion.operator.replace('_', ' ')} ${criterion.value}`);
      } else if (criterion.field === 'source') {
        parts.push(`leads from ${criterion.value}`);
      } else if (criterion.field === 'lastEngaged') {
        parts.push('recently engaged leads');
      } else if (criterion.field === 'location') {
        parts.push(`leads in ${criterion.value}`);
      } else if (criterion.field === 'tags') {
        parts.push(`leads tagged as ${criterion.value}`);
      }
    });
    
    return parts.join(' and ');
  };

  const generateMockAiInterpretation = (description) => {
    // Enhanced AI interpretation logic with better pattern matching
    const lowerDesc = description.toLowerCase();
    const mockCriteria = [];
    let confidence = 60; // Start with lower base confidence
    const explanationParts = [];
    
    // Status-based criteria
    if (lowerDesc.includes('hot')) {
      mockCriteria.push({
        id: Date.now().toString(),
        field: 'status',
        operator: 'equals',
        value: 'hot',
        weight: 1,
        logicalOperator: mockCriteria.length > 0 ? 'AND' : undefined
      });
      confidence += 15;
      explanationParts.push('hot leads');
    } else if (lowerDesc.includes('warm')) {
      mockCriteria.push({
        id: Date.now().toString(),
        field: 'status',
        operator: 'equals',
        value: 'warm',
        weight: 1,
        logicalOperator: mockCriteria.length > 0 ? 'AND' : undefined
      });
      confidence += 12;
      explanationParts.push('warm leads');
    } else if (lowerDesc.includes('cold')) {
      mockCriteria.push({
        id: Date.now().toString(),
        field: 'status',
        operator: 'equals',
        value: 'cold',
        weight: 1,
        logicalOperator: mockCriteria.length > 0 ? 'AND' : undefined
      });
      confidence += 10;
      explanationParts.push('cold leads');
    }
    
    // Score-based criteria with more sophisticated matching
    const scorePatterns = [
      { pattern: /score[s]?\s*(?:above|over|greater than|>)\s*(\d+)/i, operator: 'greater_than' },
      { pattern: /score[s]?\s*(?:below|under|less than|<)\s*(\d+)/i, operator: 'less_than' },
      { pattern: /score[s]?\s*(?:equals?|=)\s*(\d+)/i, operator: 'equals' },
      { pattern: /(?:high|top)\s*score[s]?/i, operator: 'greater_than', value: 70 },
      { pattern: /(?:low|bottom)\s*score[s]?/i, operator: 'less_than', value: 40 }
    ];
    
    for (const { pattern, operator, value } of scorePatterns) {
      const match = lowerDesc.match(pattern);
      if (match) {
        const scoreValue = value || parseInt(match[1]);
        if (scoreValue >= 0 && scoreValue <= 100) {
          mockCriteria.push({
            id: (Date.now() + mockCriteria.length + 1).toString(),
            field: 'leadScore',
            operator,
            value: scoreValue,
            weight: 1,
            logicalOperator: mockCriteria.length > 0 ? 'AND' : undefined
          });
          confidence += 18;
          explanationParts.push(`lead score ${operator.replace('_', ' ')} ${scoreValue}`);
          break;
        }
      }
    }
    
    // Source-based criteria
    const sources = ['website form', 'social media', 'referral', 'direct', 'advertisement'];
    sources.forEach(source => {
      if (lowerDesc.includes(source)) {
        mockCriteria.push({
          id: (Date.now() + mockCriteria.length + 1).toString(),
          field: 'source',
          operator: 'contains',
          value: source.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          weight: 1,
          logicalOperator: mockCriteria.length > 0 ? 'AND' : undefined
        });
        confidence += 12;
        explanationParts.push(`from ${source}`);
      }
    });
    
    // Location-based criteria
    const locationPatterns = [
      /(?:from|in|located in)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g,
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:leads|prospects|customers)/g
    ];
    
    locationPatterns.forEach(pattern => {
      const matches = [...lowerDesc.matchAll(pattern)];
      matches.forEach(match => {
        const location = match[1];
        if (location && location.length > 2) {
          mockCriteria.push({
            id: (Date.now() + mockCriteria.length + 1).toString(),
            field: 'location',
            operator: 'contains',
            value: location,
            weight: 1,
            logicalOperator: mockCriteria.length > 0 ? 'AND' : undefined
          });
          confidence += 10;
          explanationParts.push(`in ${location}`);
        }
      });
    });
    
    // Engagement-based criteria
    const engagementPatterns = [
      { pattern: /(?:recent|recently|last|past)\s*(?:engaged|active|activity)/i, days: 30 },
      { pattern: /engaged?\s*(?:in\s*)?(?:last|past)\s*(\d+)\s*days?/i, extract: true },
      { pattern: /active\s*(?:in\s*)?(?:last|past)\s*(\d+)\s*days?/i, extract: true },
      { pattern: /(?:highly?\s*)?engaged?/i, days: 60 }
    ];
    
    for (const { pattern, days, extract } of engagementPatterns) {
      const match = lowerDesc.match(pattern);
      if (match) {
        const daysAgo = extract && match[1] ? parseInt(match[1]) : days;
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
        
        mockCriteria.push({
          id: (Date.now() + mockCriteria.length + 1).toString(),
          field: 'lastEngaged',
          operator: 'greater_than',
          value: cutoffDate.toISOString().split('T')[0],
          weight: 1,
          logicalOperator: mockCriteria.length > 0 ? 'AND' : undefined
        });
        confidence += 14;
        explanationParts.push(`engaged in last ${daysAgo} days`);
        break;
      }
    }
    
    // Tag-based criteria
    const tagPatterns = [
      /tagged?\s*(?:as|with)\s*["']([^"']+)["']/gi,
      /with\s*tags?\s*["']([^"']+)["']/gi,
      /(?:vip|premium|priority)\s*(?:leads?|customers?)/gi
    ];
    
    tagPatterns.forEach(pattern => {
      const matches = [...lowerDesc.matchAll(pattern)];
      matches.forEach(match => {
        const tag = match[1] || (match[0].includes('vip') ? 'VIP' : 
                                match[0].includes('premium') ? 'Premium' : 'Priority');
        if (tag) {
          mockCriteria.push({
            id: (Date.now() + mockCriteria.length + 1).toString(),
            field: 'tags',
            operator: 'contains',
            value: tag,
            weight: 1,
            logicalOperator: mockCriteria.length > 0 ? 'AND' : undefined
          });
          confidence += 8;
          explanationParts.push(`tagged as ${tag}`);
        }
      });
    });
    
    // Adjust confidence based on criteria count and specificity
    if (mockCriteria.length === 0) {
      confidence = 20;
      explanationParts.push('general audience (no specific criteria detected)');
    } else if (mockCriteria.length >= 3) {
      confidence += 10; // Bonus for multiple criteria
    }
    
    // Cap confidence at 95%
    confidence = Math.min(confidence, 95);
    
    // Generate explanation
    const explanation = explanationParts.length > 0 
      ? `I interpreted your description to target leads that are ${explanationParts.join(' and ')}.`
      : 'I couldn\'t identify specific criteria from your description. Try being more specific about lead status, scores, sources, or engagement.';
    
    return {
      criteria: mockCriteria,
      confidence,
      explanation,
      suggestions: mockCriteria.length === 0 ? [
        'Try mentioning lead status (hot, warm, cold)',
        'Include score ranges (e.g., "score above 70")',
        'Specify sources (e.g., "from social media")',
        'Add engagement criteria (e.g., "recently engaged")'
      ] : []
    };
  };

  if (!isOpen) return null;

  const completedCriteriaCount = criteria.filter(c => c.field && c.operator && (c.value !== '' && c.value !== null && c.value !== undefined)).length;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/70 dark:bg-black/80 backdrop-blur-[4px] flex items-center justify-center z-[200] p-4">
      <div className="bg-surface dark:bg-dark-800 border border-line dark:border-white/[0.07] rounded-2xl shadow-2xl dark:shadow-[0_32px_80px_rgba(0,0,0,0.6)] w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* ── Premium Header ────────────────────────────────────── */}
        <div className="relative px-6 py-5 border-b border-line-subtle dark:border-white/[0.07] overflow-hidden">
          {/* Background glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50/60 via-transparent to-violet-50/40 dark:from-indigo-500/[0.06] dark:via-transparent dark:to-violet-500/[0.04] pointer-events-none" />

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Icon */}
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shadow-md flex-shrink-0 ${
                editingSegment
                  ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/20'
                  : 'bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-indigo-500/25'
              }`}>
                <Target className="w-5 h-5 text-white" />
              </div>

              <div>
                <h2 className="text-lg font-bold text-content dark:text-white tracking-tight">
                  {editingSegment ? 'Edit Audience Segment' : 'Create Audience Segment'}
                </h2>
                <p className="text-sm text-content-muted dark:text-gray-400 mt-0.5">
                  {editingSegment
                    ? `Last modified: ${new Date(editingSegment.updatedAt).toLocaleDateString()}`
                    : 'Build precise audience segments with rules or AI assistance'}
                </p>
              </div>
            </div>

            {/* Right side: mini stats + close */}
            <div className="flex items-center gap-4">
              {/* Live stats pills */}
              <div className="hidden sm:flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 text-xs font-semibold text-indigo-700 dark:text-indigo-400">
                  <Users className="w-3 h-3" />
                  {criteria.length} rule{criteria.length !== 1 ? 's' : ''}
                </span>
                {previewData && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                    <Eye className="w-3 h-3" />
                    {previewData.estimatedSize?.toLocaleString() ?? 0} leads
                  </span>
                )}
              </div>

              <button
                onClick={handleClose}
                className="p-2 rounded-xl text-content-subtle dark:text-gray-600 hover:text-content-secondary dark:hover:text-content-subtle hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* ── Tab Navigation ────────────────────────────────────── */}
        <div className="flex border-b border-line-subtle dark:border-white/[0.07] bg-gray-50/60 dark:bg-white/[0.01]">
          <button
            onClick={() => setActiveTab('manual')}
            className={`relative flex-1 px-6 py-3.5 text-sm font-semibold transition-all ${
              activeTab === 'manual'
                ? 'text-blue-600 dark:text-indigo-400 bg-surface dark:bg-dark-800'
                : 'text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white/60 dark:hover:bg-white/[0.02]'
            }`}
          >
            {activeTab === 'manual' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-500 dark:from-blue-400 dark:to-indigo-400 rounded-t-full" />
            )}
            <div className="flex items-center justify-center gap-2">
              <Users className="w-4 h-4" />
              Manual Creation
              {criteria.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 text-[10px] font-bold">
                  {criteria.length}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`relative flex-1 px-6 py-3.5 text-sm font-semibold transition-all ${
              activeTab === 'ai'
                ? 'text-violet-600 dark:text-violet-400 bg-surface dark:bg-dark-800'
                : 'text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white/60 dark:hover:bg-white/[0.02]'
            }`}
          >
            {activeTab === 'ai' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-500 dark:from-violet-400 dark:to-purple-400 rounded-t-full" />
            )}
            <div className="flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4" />
              AI-Assisted
              <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-md bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-400 text-[9px] font-bold tracking-wide uppercase">
                Beta
              </span>
            </div>
          </button>
        </div>

        {/* ── Scrollable Content ────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="p-4 rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-red-800 dark:text-red-300">Please fix the following errors:</span>
                </div>
                <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}

            {activeTab === 'manual' ? (
              <ManualCreationTab
                criteria={criteria}
                onAddCriterion={addCriterion}
                onUpdateCriterion={updateCriterion}
                onRemoveCriterion={removeCriterion}
                leads={leads}
              />
            ) : (
              <AIAssistedTab
                description={aiDescription}
                onDescriptionChange={setAiDescription}
                onProcess={processAiDescription}
                interpretation={aiInterpretation}
                confidence={aiConfidence}
                isProcessing={isAiProcessing}
                criteria={criteria}
                onUpdateCriterion={updateCriterion}
              />
            )}

            {/* Enhanced Preview Section with AI insights */}
            {(criteria.length > 0 || previewData) && (
              <EnhancedSegmentPreview
                previewData={previewData}
                isLoading={isPreviewLoading}
                totalLeads={leads.length}
                criteria={criteria}
                leads={leads}
                isAiGenerated={activeTab === 'ai'}
                aiConfidence={aiConfidence}
              />
            )}
          </div>

          {/* ── Footer ─────────────────────────────────────────── */}
          <div className="border-t border-line-subtle dark:border-white/[0.07] px-6 py-5 bg-gray-50/50 dark:bg-white/[0.01]">
            <SegmentSaveForm
              segmentName={segmentName}
              setSegmentName={setSegmentName}
              segmentDescription={segmentDescription}
              setSegmentDescription={setSegmentDescription}
              segmentTags={segmentTags}
              setSegmentTags={setSegmentTags}
              existingSegments={existingSegments}
              editingSegment={editingSegment}
              validationErrors={validationErrors}
              previewData={previewData}
            />

            <div className="flex items-center justify-between mt-6">
              {/* Left: completion hint */}
              <div className="flex items-center gap-2 text-xs text-content-subtle dark:text-gray-600">
                {completedCriteriaCount > 0 && (
                  <>
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                    <span>{completedCriteriaCount} of {criteria.length} rule{criteria.length !== 1 ? 's' : ''} complete</span>
                  </>
                )}
              </div>

              {/* Right: action buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 rounded-xl border border-line dark:border-white/[0.08] text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className={`px-5 py-2 rounded-xl text-white text-sm font-semibold transition-all shadow-sm active:scale-[0.98] ${
                    editingSegment
                      ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-indigo-500/20'
                  }`}
                >
                  {editingSegment ? 'Update Segment' : 'Create Segment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  , document.body);
};

// Enhanced Manual Creation Tab Component with better UX
const ManualCreationTab = ({ 
  criteria, 
  onAddCriterion, 
  onUpdateCriterion, 
  onRemoveCriterion,
  leads 
}) => {
  const [criteriaErrors, setCriteriaErrors] = useState({});

  // Validate criteria in real-time
  useEffect(() => {
    const errors = {};
    criteria.forEach((criterion, index) => {
      const validation = validateSingleCriterion(criterion, index);
      if (validation.error) {
        errors[criterion.id] = validation.error;
      }
    });
    setCriteriaErrors(errors);
  }, [criteria]);

  const validateSingleCriterion = (criterion, index) => {
    if (!criterion.field) {
      return { error: 'Field is required' };
    }
    if (!criterion.operator) {
      return { error: 'Operator is required' };
    }
    
    if (criterion.operator === 'between') {
      if (!Array.isArray(criterion.value) || criterion.value.length !== 2 || 
          criterion.value[0] === '' || criterion.value[1] === '') {
        return { error: 'Both values required for between operator' };
      }
    } else if (['in', 'not_in'].includes(criterion.operator)) {
      if (!Array.isArray(criterion.value) || criterion.value.length === 0) {
        return { error: 'At least one value required' };
      }
    } else if (!criterion.value && criterion.value !== 0) {
      return { error: 'Value is required' };
    }

    if (index > 0 && !criterion.logicalOperator) {
      return { error: 'Logical operator required' };
    }

    return { error: null };
  };

  const hasErrors = Object.keys(criteriaErrors).length > 0;
  const completedCriteria = criteria.filter(c => 
    c.field && c.operator && (c.value || c.value === 0 || Array.isArray(c.value))
  ).length;

  return (
    <div className="space-y-6">
      {/* Header with status */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-content dark:text-white">Define Criteria</h3>
          <p className="text-sm text-content-muted dark:text-gray-400 mt-0.5">
            {criteria.length === 0
              ? 'Start by adding your first criterion'
              : `${completedCriteria} of ${criteria.length} criteria completed`
            }
          </p>
        </div>
        <button
          onClick={onAddCriterion}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-blue-600 text-white text-sm font-semibold transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Criterion
        </button>
      </div>

      {/* Validation summary */}
      {hasErrors && criteria.length > 0 && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {Object.keys(criteriaErrors).length} criteria need attention
            </span>
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-400">
            Complete all required fields to see segment preview
          </p>
        </div>
      )}

      {/* Criteria list or empty state */}
      {criteria.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-white/[0.02] rounded-2xl border-2 border-dashed border-line dark:border-white/[0.08]">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <h4 className="text-lg font-semibold text-content dark:text-white mb-2">No criteria defined yet</h4>
          <p className="text-content-muted dark:text-gray-400 mb-4">
            Create targeted segments by defining specific criteria for your leads
          </p>
          <button
            onClick={onAddCriterion}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-blue-600 text-white font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Your First Criterion
          </button>
          
          {/* Quick start suggestions */}
          <div className="mt-6 text-left max-w-md mx-auto">
            <h5 className="text-sm font-semibold text-content dark:text-white mb-2">Popular criteria to get started:</h5>
            <div className="space-y-1 text-xs text-content-muted dark:text-gray-400">
              <div>• Lead Status = "hot" (target high-quality leads)</div>
              <div>• Lead Score {'>'}  70 (focus on engaged prospects)</div>
              <div>• Last Engaged {'>'}  recent date (active leads)</div>
              <div>• Source = "Website Form" (specific acquisition channel)</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {criteria.map((criterion, index) => (
            <div key={criterion.id} className="relative">
              <CriterionBuilder
                criterion={criterion}
                index={index}
                onUpdate={(updates) => onUpdateCriterion(criterion.id, updates)}
                onRemove={() => onRemoveCriterion(criterion.id)}
                showLogicalOperator={index > 0}
                leads={leads}
              />
              
              {/* Criterion status indicator */}
              <div className="absolute top-4 right-12 flex items-center">
                {criteriaErrors[criterion.id] ? (
                  <div className="flex items-center gap-1 text-red-600 dark:text-red-400" title={criteriaErrors[criterion.id]}>
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-xs">Incomplete</span>
                  </div>
                ) : criterion.field && criterion.operator && (criterion.value || criterion.value === 0 || Array.isArray(criterion.value)) ? (
                  <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-xs">Complete</span>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
          
          {/* Add another criterion hint */}
          {criteria.length > 0 && !hasErrors && (
            <div className="text-center py-4">
              <button
                onClick={onAddCriterion}
                className="text-blue-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-blue-300 text-sm font-semibold flex items-center gap-1 mx-auto transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add another criterion to refine your segment
              </button>
            </div>
          )}
        </div>
      )}

      {/* Criteria summary */}
      {criteria.length > 0 && (
        <div className="bg-indigo-50 dark:bg-indigo-500/10 rounded-xl border border-indigo-100 dark:border-indigo-500/20 p-4">
          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">Segment Logic Summary</h4>
          <div className="text-sm text-blue-800 dark:text-blue-200">
            {criteria.map((criterion, index) => (
              <div key={criterion.id} className="flex items-center gap-2">
                {index > 0 && (
                  <span className="font-medium text-blue-600 dark:text-blue-300">
                    {criterion.logicalOperator || 'AND'}
                  </span>
                )}
                <span>
                  {criterion.field ? (
                    <>
                      <span className="font-medium">{criterion.field}</span>
                      {criterion.operator && <span> {criterion.operator} </span>}
                      {criterion.value && (
                        <span className="font-medium">
                          {Array.isArray(criterion.value) 
                            ? criterion.value.join(', ') 
                            : criterion.value}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-content-muted italic">Incomplete criterion</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced AI-Assisted Tab Component with comprehensive features
const AIAssistedTab = ({ 
  description, 
  onDescriptionChange, 
  onProcess, 
  interpretation, 
  confidence, 
  isProcessing,
  criteria,
  onUpdateCriterion,
  leads = []
}) => {
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // Predefined templates for common segment types
  const segmentTemplates = [
    {
      id: 'high-value',
      name: 'High-Value Prospects',
      description: 'Hot leads with high scores who have engaged recently',
      icon: '🔥'
    },
    {
      id: 'nurture-ready',
      name: 'Nurture-Ready Leads',
      description: 'Warm leads from specific sources that need follow-up',
      icon: '🌱'
    },
    {
      id: 'geographic',
      name: 'Geographic Segment',
      description: 'Leads from specific locations or regions',
      icon: '📍'
    },
    {
      id: 'engagement-based',
      name: 'Engagement-Based',
      description: 'Leads based on recent activity and interaction patterns',
      icon: '⚡'
    },
    {
      id: 'source-specific',
      name: 'Source-Specific',
      description: 'Leads from particular acquisition channels',
      icon: '🎯'
    }
  ];

  // Generate AI suggestions based on current leads data
  useEffect(() => {
    if (leads.length > 0) {
      generateAiSuggestions();
    }
  }, [leads]);

  const generateAiSuggestions = () => {
    const suggestions = [];
    
    // Analyze lead distribution
    const statusCounts = leads.reduce((acc, lead) => {
      acc[lead.status] = (acc[lead.status] || 0) + 1;
      return acc;
    }, {});
    
    const sourceCounts = leads.reduce((acc, lead) => {
      acc[lead.source] = (acc[lead.source] || 0) + 1;
      return acc;
    }, {});

    // Suggest high-value segments
    if (statusCounts.hot > 0) {
      suggestions.push({
        type: 'high-value',
        text: `Target your ${statusCounts.hot} hot leads with high engagement`,
        confidence: 90
      });
    }

    // Suggest source-based segments
    const topSource = Object.entries(sourceCounts).sort(([,a], [,b]) => b - a)[0];
    if (topSource && topSource[1] > 5) {
      suggestions.push({
        type: 'source-based',
        text: `Create segment for ${topSource[1]} leads from ${topSource[0]}`,
        confidence: 85
      });
    }

    // Suggest score-based segments
    const highScoreLeads = leads.filter(lead => lead.leadScore > 70).length;
    if (highScoreLeads > 0) {
      suggestions.push({
        type: 'score-based',
        text: `Target ${highScoreLeads} leads with scores above 70`,
        confidence: 88
      });
    }

    setAiSuggestions(suggestions.slice(0, 3));
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template.id);
    onDescriptionChange(template.description);
  };

  const handleSuggestionClick = (suggestion) => {
    onDescriptionChange(suggestion.text);
  };

  return (
    <div className="space-y-6">
      {/* Header with AI branding */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-500/10 dark:to-blue-500/10 rounded-xl border border-purple-200 dark:border-purple-500/20 p-5">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 bg-purple-600 dark:bg-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-base font-bold text-content dark:text-white">AI-Assisted Segment Creation</h3>
            <p className="text-sm text-content-muted dark:text-gray-400">Describe your ideal audience in natural language</p>
          </div>
        </div>
      </div>

      {/* Quick Templates */}
      <div className="space-y-3">
        <h4 className="text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide">Quick Start Templates</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {segmentTemplates.map(template => (
            <button
              key={template.id}
              onClick={() => handleTemplateSelect(template)}
              className={`text-left p-3 rounded-xl border transition-all hover:shadow-sm ${
                selectedTemplate === template.id
                  ? 'border-purple-300 dark:border-purple-500/40 bg-purple-50 dark:bg-purple-500/10'
                  : 'border-line dark:border-white/[0.08] hover:border-line-strong dark:hover:border-white/[0.14] bg-surface dark:bg-white/[0.02]'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">{template.icon}</span>
                <span className="text-sm font-semibold text-content dark:text-white">{template.name}</span>
              </div>
              <p className="text-xs text-content-muted dark:text-gray-400">{template.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* AI Suggestions based on current data */}
      {aiSuggestions.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide">AI Suggestions Based on Your Data</h4>
          <div className="space-y-2">
            {aiSuggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left p-3 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-xl hover:bg-blue-100 dark:hover:bg-indigo-500/[0.15] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-content dark:text-white">{suggestion.text}</span>
                  <div className="flex items-center gap-1.5 ml-3">
                    <div className="w-12 h-1.5 bg-gray-200 dark:bg-white/[0.1] rounded-full">
                      <div 
                        className="h-full bg-indigo-500 rounded-full"
                        style={{ width: `${suggestion.confidence}%` }}
                      />
                    </div>
                    <span className="text-xs text-content-muted dark:text-gray-400">{suggestion.confidence}%</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Natural Language Input */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Describe Your Target Audience
          </label>
          <textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 text-content dark:text-white placeholder:text-content-subtle dark:placeholder:text-content-subtle focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 dark:focus:border-purple-400 resize-none outline-none transition-all"
            rows="4"
            placeholder="Example: 'Hot leads with scores above 80 who have engaged in the last 30 days' or 'Warm prospects from social media in California with high engagement'"
          />
          <div className="mt-2 text-xs text-content-muted dark:text-gray-500">
            💡 Tip: Be specific about lead status, scores, locations, sources, or engagement patterns
          </div>
        </div>

        {/* Advanced Options Toggle */}
        <button
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold flex items-center gap-1 transition-colors"
        >
          {showAdvancedOptions ? 'Hide' : 'Show'} Advanced Options
          <span className={`transform transition-transform ${showAdvancedOptions ? 'rotate-180' : ''}`}>
            ▼
          </span>
        </button>

        {/* Advanced Options */}
        {showAdvancedOptions && (
          <div className="bg-gray-50 dark:bg-white/[0.02] rounded-xl border border-line dark:border-white/[0.08] p-4 space-y-3">
            <h5 className="text-sm font-semibold text-content dark:text-white">AI Processing Options</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-content-secondary dark:text-gray-400 mb-1">
                  Interpretation Style
                </label>
                <SelectField className="w-full px-3 py-2 text-sm rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 text-content dark:text-white focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 dark:focus:border-purple-400 outline-none">
                  <option value="balanced">Balanced (Recommended)</option>
                  <option value="strict">Strict Matching</option>
                  <option value="broad">Broad Interpretation</option>
                </SelectField>
              </div>
              <div>
                <label className="block text-xs font-semibold text-content-secondary dark:text-gray-400 mb-1">
                  Confidence Threshold
                </label>
                <SelectField className="w-full px-3 py-2 text-sm rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 text-content dark:text-white focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 dark:focus:border-purple-400 outline-none">
                  <option value="60">60% (More suggestions)</option>
                  <option value="70" selected>70% (Balanced)</option>
                  <option value="80">80% (High confidence only)</option>
                </SelectField>
              </div>
            </div>
          </div>
        )}
        
        <button
          onClick={onProcess}
          disabled={!description.trim() || isProcessing}
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          <Brain className="w-4 h-4" />
          {isProcessing ? (
            <>
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              Processing...
            </>
          ) : (
            'Generate Criteria with AI'
          )}
        </button>
      </div>

      {/* AI Processing Status */}
      {isProcessing && (
        <div className="bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="animate-pulse w-2 h-2 bg-purple-600 dark:bg-purple-400 rounded-full flex-shrink-0"></div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-purple-900 dark:text-purple-300">AI is analyzing your description...</div>
              <div className="text-xs text-purple-700 dark:text-purple-400 mt-1">
                Interpreting natural language and matching to available lead data
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Interpretation Results */}
      {interpretation && !isProcessing && (
        <AIInterpretationResults
          interpretation={interpretation}
          confidence={confidence}
          criteria={criteria}
          onUpdateCriterion={onUpdateCriterion}
          leads={leads}
        />
      )}
    </div>
  );
};

// AI Interpretation Results Component with enhanced display and manual override
const AIInterpretationResults = ({ 
  interpretation, 
  confidence, 
  criteria, 
  onUpdateCriterion,
  leads 
}) => {
  const [showCriteriaEditor, setShowCriteriaEditor] = useState(false);
  const [editingCriterion, setEditingCriterion] = useState(null);

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30';
    if (confidence >= 60) return 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 80) return 'High Confidence';
    if (confidence >= 60) return 'Medium Confidence';
    return 'Low Confidence';
  };

  const handleCriterionEdit = (criterion) => {
    setEditingCriterion(criterion);
    setShowCriteriaEditor(true);
  };

  const handleCriterionUpdate = (criterionId, updates) => {
    onUpdateCriterion(criterionId, updates);
    setEditingCriterion(null);
    setShowCriteriaEditor(false);
  };

  return (
    <div className="space-y-4">
      {/* Confidence Score Header */}
      <div className="rounded-xl border border-line dark:border-white/[0.07] bg-surface dark:bg-white/[0.02] p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-content dark:text-white flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            AI Interpretation Complete
          </h4>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getConfidenceColor(confidence)}`}>
              {getConfidenceLabel(confidence)}
            </span>
            <div className="flex items-center gap-1.5">
              <div className="w-20 h-2 bg-gray-200 dark:bg-white/[0.1] rounded-full">
                <div 
                  className={`h-full rounded-full transition-all ${
                    confidence >= 80 ? 'bg-emerald-500' : 
                    confidence >= 60 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${confidence}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-content dark:text-white">{confidence}%</span>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-content-secondary dark:text-gray-300 mb-4">{interpretation.explanation}</p>
        
        {/* Confidence explanation */}
        <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-xl p-3">
          <div className="text-xs text-blue-800 dark:text-blue-300">
            <strong>Confidence Score Explanation:</strong> Based on how well your description matches 
            available lead data fields and the specificity of your criteria. 
            {confidence < 70 && " Consider adding more specific details for better results."}
          </div>
        </div>
      </div>

      {/* Generated Criteria with Manual Override */}
      {criteria.length > 0 && (
        <div className="rounded-xl border border-line dark:border-white/[0.07] bg-surface dark:bg-white/[0.02] p-4">
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-sm font-semibold text-content dark:text-white">Generated Criteria</h5>
            <button
              onClick={() => setShowCriteriaEditor(!showCriteriaEditor)}
              className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold transition-colors"
            >
              {showCriteriaEditor ? 'Hide Editor' : 'Manual Override'}
            </button>
          </div>
          
          <div className="space-y-2">
            {criteria.map((criterion, index) => (
              <div key={criterion.id} className="group">
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-white/[0.03] rounded-xl hover:bg-gray-100 dark:hover:bg-white/[0.05] transition-colors">
                  <div className="flex items-center gap-2">
                    {index > 0 && (
                      <span className="px-2 py-1 bg-blue-100 dark:bg-indigo-500/20 text-blue-800 dark:text-blue-300 text-xs font-semibold rounded-lg">
                        {criterion.logicalOperator}
                      </span>
                    )}
                    <div className="text-sm">
                      <span className="font-semibold text-content dark:text-white capitalize">{criterion.field}</span>
                      <span className="text-content-muted dark:text-gray-400 mx-2">{criterion.operator}</span>
                      <span className="font-semibold text-content dark:text-white">
                        {Array.isArray(criterion.value) 
                          ? criterion.value.join(', ') 
                          : criterion.value}
                      </span>
                    </div>
                  </div>
                  
                  {showCriteriaEditor && (
                    <button
                      onClick={() => handleCriterionEdit(criterion)}
                      className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-semibold transition-all"
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Quality Insights */}
          <div className="mt-4 pt-4 border-t border-line-subtle dark:border-white/[0.05]">
            <h6 className="text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide mb-2">AI Quality Insights</h6>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-xl p-3">
                <div className="font-semibold text-blue-900 dark:text-blue-300">Specificity</div>
                <div className="text-indigo-700 dark:text-indigo-400">
                  {criteria.length >= 3 ? 'Highly Specific' : 
                   criteria.length >= 2 ? 'Moderately Specific' : 'Basic'}
                </div>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 rounded-xl p-3">
                <div className="font-semibold text-emerald-900 dark:text-emerald-300">Targeting</div>
                <div className="text-emerald-700 dark:text-emerald-400">
                  {confidence >= 80 ? 'Precise' : 
                   confidence >= 60 ? 'Good' : 'Broad'}
                </div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-500/10 border border-purple-100 dark:border-purple-500/20 rounded-xl p-3">
                <div className="font-semibold text-purple-900 dark:text-purple-300">Complexity</div>
                <div className="text-purple-700 dark:text-purple-400">
                  {criteria.some(c => c.logicalOperator === 'OR') ? 'Advanced' : 'Simple'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Override Editor Modal */}
      {showCriteriaEditor && editingCriterion && (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/75 backdrop-blur-[3px] flex items-center justify-center z-[210] p-4">
          <div className="bg-surface dark:bg-dark-800 border border-line dark:border-white/[0.07] rounded-2xl shadow-2xl dark:shadow-[0_20px_60px_rgba(0,0,0,0.5)] w-full max-w-md">
            <div className="p-5 border-b border-line-subtle dark:border-white/[0.07]">
              <h3 className="text-base font-bold text-content dark:text-white">Edit Criterion</h3>
            </div>
            <div className="p-5">
              <CriterionEditor
                criterion={editingCriterion}
                onUpdate={(updates) => handleCriterionUpdate(editingCriterion.id, updates)}
                onCancel={() => {
                  setEditingCriterion(null);
                  setShowCriteriaEditor(false);
                }}
                leads={leads}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Criterion Editor Component for manual overrides
const CriterionEditor = ({ criterion, onUpdate, onCancel, leads }) => {
  const [field, setField] = useState(criterion.field);
  const [operator, setOperator] = useState(criterion.operator);
  const [value, setValue] = useState(criterion.value);
  const [logicalOperator, setLogicalOperator] = useState(criterion.logicalOperator);

  const allFields = [
    ...SEGMENT_FIELDS.basic_info,
    ...SEGMENT_FIELDS.lead_qualification,
    ...SEGMENT_FIELDS.engagement
  ];

  const selectedField = allFields.find(f => f.key === field);
  const availableOperators = selectedField?.operators || Object.keys(OPERATORS);

  const handleSave = () => {
    onUpdate({
      field,
      operator,
      value,
      logicalOperator
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Field</label>
        <SelectField
          value={field}
          onChange={(e) => setField(e.target.value)}
          className="w-full px-3 py-2 rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 text-content dark:text-white focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 dark:focus:border-purple-400 outline-none"
        >
          {allFields.map(f => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </SelectField>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Operator</label>
        <SelectField
          value={operator}
          onChange={(e) => setOperator(e.target.value)}
          className="w-full px-3 py-2 rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 text-content dark:text-white focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 dark:focus:border-purple-400 outline-none"
        >
          {availableOperators.map(op => (
            <option key={op} value={op}>{OPERATORS[op]?.label || op}</option>
          ))}
        </SelectField>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Value</label>
        <input
          type="text"
          value={Array.isArray(value) ? value.join(', ') : value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full px-3 py-2 rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 text-content dark:text-white placeholder:text-content-subtle dark:placeholder:text-content-subtle focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 dark:focus:border-purple-400 outline-none"
          placeholder="Enter value"
        />
      </div>

      {criterion.logicalOperator && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Logical Operator</label>
          <SelectField
            value={logicalOperator}
            onChange={(e) => setLogicalOperator(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 text-content dark:text-white focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 dark:focus:border-purple-400 outline-none"
          >
            <option value="AND">AND</option>
            <option value="OR">OR</option>
          </SelectField>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-xl border border-line dark:border-white/[0.08] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.04] font-semibold transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white font-semibold transition-colors shadow-sm"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

// Enhanced Criterion Builder Component with comprehensive field support
const CriterionBuilder = ({ 
  criterion, 
  index, 
  onUpdate, 
  onRemove, 
  showLogicalOperator,
  leads 
}) => {
  const [validationError, setValidationError] = useState('');
  
  const allFields = [
    ...SEGMENT_FIELDS.basic_info,
    ...SEGMENT_FIELDS.lead_qualification,
    ...SEGMENT_FIELDS.engagement
  ];

  const selectedField = allFields.find(f => f.key === criterion.field);
  const availableOperators = selectedField?.operators || Object.keys(OPERATORS);

  // Get unique values for select fields from actual leads data
  const getFieldOptions = (fieldKey) => {
    if (!leads || leads.length === 0) return [];
    
    const values = leads.map(lead => {
      const value = lead[fieldKey];
      if (Array.isArray(value)) {
        return value; // For tags and multi-value fields
      }
      return value;
    }).flat().filter(Boolean);
    
    return [...new Set(values)].sort();
  };

  // Enhanced validation for criterion values
  const validateCriterion = (field, operator, value) => {
    if (!field) return 'Field is required';
    if (!operator) return 'Operator is required';
    
    if (operator === 'between') {
      if (!Array.isArray(value) || value.length !== 2) {
        return 'Between operator requires two values';
      }
      if (value[0] === '' || value[1] === '') {
        return 'Both values are required for between operator';
      }
    } else if (['in', 'not_in'].includes(operator)) {
      if (!Array.isArray(value) || value.length === 0) {
        return `${operator === 'in' ? 'Is one of' : 'Is not one of'} requires at least one value`;
      }
    } else if (value === '' || value === null || value === undefined) {
      return 'Value is required';
    }
    
    return '';
  };

  // Handle field change with validation
  const handleFieldChange = (newField) => {
    onUpdate({ field: newField, value: '', operator: 'equals' });
    setValidationError('');
  };

  // Handle operator change with value reset
  const handleOperatorChange = (newOperator) => {
    let newValue = '';
    if (newOperator === 'between') {
      newValue = ['', ''];
    } else if (['in', 'not_in'].includes(newOperator)) {
      newValue = [];
    }
    onUpdate({ operator: newOperator, value: newValue });
    setValidationError('');
  };

  // Handle value change with validation
  const handleValueChange = (newValue) => {
    onUpdate({ value: newValue });
    const error = validateCriterion(criterion.field, criterion.operator, newValue);
    setValidationError(error);
  };

  // Render value input based on field type and operator
  const renderValueInput = () => {
    if (!selectedField) return null;

    const baseInputClass = `w-full px-3 py-2 rounded-xl border focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 dark:focus:border-blue-400 outline-none bg-surface dark:bg-dark-700 border-line dark:border-white/[0.20] text-content dark:text-white placeholder:text-content-subtle dark:placeholder:text-content-subtle ${
      validationError ? 'border-red-300 dark:border-red-500/40' : 'border-line dark:border-white/[0.08]'
    }`;

    // Handle 'between' operator
    if (criterion.operator === 'between') {
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input
              type={selectedField.type === 'number' ? 'number' : selectedField.type === 'date' ? 'date' : 'text'}
              value={Array.isArray(criterion.value) ? criterion.value[0] || '' : ''}
              onChange={(e) => {
                const newValue = Array.isArray(criterion.value) ? [...criterion.value] : ['', ''];
                newValue[0] = e.target.value;
                handleValueChange(newValue);
              }}
              className={baseInputClass}
              placeholder="From"
            />
            <input
              type={selectedField.type === 'number' ? 'number' : selectedField.type === 'date' ? 'date' : 'text'}
              value={Array.isArray(criterion.value) ? criterion.value[1] || '' : ''}
              onChange={(e) => {
                const newValue = Array.isArray(criterion.value) ? [...criterion.value] : ['', ''];
                newValue[1] = e.target.value;
                handleValueChange(newValue);
              }}
              className={baseInputClass}
              placeholder="To"
            />
          </div>
        </div>
      );
    }

    // Handle 'in' and 'not_in' operators
    if (['in', 'not_in'].includes(criterion.operator)) {
      const availableOptions = selectedField.options || getFieldOptions(criterion.field);
      const selectedValues = Array.isArray(criterion.value) ? criterion.value : [];

      return (
        <div className="space-y-2">
          <div className="max-h-32 overflow-y-auto rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 p-2">
            {availableOptions.map(option => (
              <label key={option} className="flex items-center space-x-2 py-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    let newValue = [...selectedValues];
                    if (e.target.checked) {
                      newValue.push(option);
                    } else {
                      newValue = newValue.filter(v => v !== option);
                    }
                    handleValueChange(newValue);
                  }}
                  className="rounded border-line-strong dark:border-white/[0.2] text-blue-600 dark:text-blue-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{option}</span>
              </label>
            ))}
          </div>
          {selectedValues.length > 0 && (
            <div className="text-xs text-content-muted dark:text-gray-400">
              Selected: {selectedValues.join(', ')}
            </div>
          )}
        </div>
      );
    }

    // Handle regular field types
    if (selectedField.type === 'select') {
      const options = selectedField.options || getFieldOptions(criterion.field);
      return (
        <SelectField
          value={criterion.value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          className={baseInputClass}
        >
          <option value="">Select value</option>
          {options.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </SelectField>
      );
    }

    if (selectedField.type === 'multi-select') {
      // For tags field, show available tags from leads
      const availableTags = getFieldOptions(criterion.field);
      return (
        <SelectField
          value={criterion.value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          className={baseInputClass}
        >
          <option value="">Select tag</option>
          {availableTags.map(tag => (
            <option key={tag} value={tag}>{tag}</option>
          ))}
        </SelectField>
      );
    }

    if (selectedField.type === 'number') {
      return (
        <input
          type="number"
          value={criterion.value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          className={baseInputClass}
          placeholder="Enter number"
        />
      );
    }

    if (selectedField.type === 'date') {
      return (
        <input
          type="date"
          value={criterion.value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          className={baseInputClass}
        />
      );
    }

    // Default text input
    return (
      <input
        type="text"
        value={criterion.value || ''}
        onChange={(e) => handleValueChange(e.target.value)}
        className={baseInputClass}
        placeholder="Enter value"
      />
    );
  };

  return (
    <div className="rounded-xl border border-line dark:border-white/[0.08] bg-surface dark:bg-white/[0.02] p-4">
      {showLogicalOperator && (
        <div className="mb-4">
          <label className="block text-xs font-semibold text-content-muted dark:text-gray-500 uppercase tracking-wide mb-1.5">Logical Operator</label>
          <SelectField
            value={criterion.logicalOperator || 'AND'}
            onChange={(e) => onUpdate({ logicalOperator: e.target.value })}
            className="px-3 py-2 rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 text-content dark:text-white text-sm font-semibold focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 dark:focus:border-blue-400 outline-none"
          >
            {Object.entries(LOGICAL_OPERATORS).map(([key, op]) => (
              <option key={key} value={key}>{op.label}</option>
            ))}
          </SelectField>
          <p className="text-xs text-content-muted dark:text-gray-500 mt-1">
            {LOGICAL_OPERATORS[criterion.logicalOperator || 'AND']?.description}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
            Field <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <SelectField
            value={criterion.field || ''}
            onChange={(e) => handleFieldChange(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 text-content dark:text-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 dark:focus:border-blue-400 outline-none"
          >
            <option value="">Select field</option>
            {Object.entries(SEGMENT_FIELDS).map(([category, fields]) => (
              <optgroup key={category} label={category.replace('_', ' ').toUpperCase()}>
                {fields.map(field => (
                  <option key={field.key} value={field.key}>{field.label}</option>
                ))}
              </optgroup>
            ))}
          </SelectField>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
            Operator <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          <SelectField
            value={criterion.operator || 'equals'}
            onChange={(e) => handleOperatorChange(e.target.value)}
            className="w-full px-3 py-2 rounded-xl border border-line dark:border-white/[0.20] bg-surface dark:bg-dark-700 text-content dark:text-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 dark:focus:border-blue-400 outline-none disabled:opacity-50"
            disabled={!criterion.field}
          >
            {availableOperators.map(op => (
              <option key={op} value={op}>{OPERATORS[op]?.label || op}</option>
            ))}
          </SelectField>
          {selectedField && (
            <p className="text-xs text-content-muted dark:text-gray-500 mt-1">
              {OPERATORS[criterion.operator]?.description}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
            Value <span className="text-red-500 dark:text-red-400">*</span>
          </label>
          {renderValueInput()}
          {validationError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3 flex-shrink-0" />
              {validationError}
            </p>
          )}
        </div>

        <div className="flex items-end">
          <button
            onClick={onRemove}
            className="p-2 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-colors"
            title="Remove criterion"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Field information */}
      {selectedField && (
        <div className="mt-3 p-3 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-xl text-xs text-indigo-700 dark:text-indigo-400">
          <strong>Field Info:</strong> {selectedField.label} — Available values from your leads data
        </div>
      )}
    </div>
  );
};

// Segment Preview Component
const SegmentPreview = ({ previewData, isLoading, totalLeads }) => {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-line dark:border-white/[0.07] bg-surface dark:bg-white/[0.02] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="w-5 h-5 text-content-subtle" />
          <h4 className="font-semibold text-content dark:text-white">Segment Preview</h4>
        </div>
        <div className="text-center py-4">
          <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-sm text-content-muted dark:text-gray-400 mt-2">Calculating preview...</p>
        </div>
      </div>
    );
  }

  if (!previewData) {
    return null;
  }

  return (
    <div className="rounded-xl border border-line dark:border-white/[0.07] bg-surface dark:bg-white/[0.02] p-6">
      <div className="flex items-center gap-2 mb-4">
        <Eye className="w-5 h-5 text-blue-600 dark:text-indigo-400" />
        <h4 className="font-semibold text-content dark:text-white">Segment Preview</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Size and Quality */}
        <div>
          <div className="text-2xl font-bold text-blue-600 dark:text-indigo-400">{previewData.estimatedSize}</div>
          <div className="text-sm text-content-muted dark:text-gray-400">
            leads ({previewData.percentage}% of {totalLeads} total)
          </div>
          <div className="mt-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-content-muted dark:text-gray-400">Quality Score:</span>
              <div className="flex items-center gap-1.5">
                <div className="w-16 h-2 bg-gray-200 dark:bg-white/[0.1] rounded-full">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      previewData.qualityScore >= 70 ? 'bg-emerald-500' :
                      previewData.qualityScore >= 40 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${previewData.qualityScore}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-content dark:text-white">{previewData.qualityScore}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sample Profiles */}
        <div>
          <h5 className="font-semibold text-content dark:text-white mb-2">Sample Leads</h5>
          {previewData.sampleProfiles.length > 0 ? (
            <div className="space-y-1">
              {previewData.sampleProfiles.slice(0, 3).map(lead => (
                <div key={lead.id} className="text-sm text-content-muted dark:text-gray-400">
                  {lead.name} — {lead.status} ({lead.leadScore})
                </div>
              ))}
              {previewData.sampleProfiles.length > 3 && (
                <div className="text-xs text-content-subtle dark:text-gray-500">
                  +{previewData.sampleProfiles.length - 3} more
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-content-subtle dark:text-gray-500">No matching leads</div>
          )}
        </div>

        {/* Recommendations */}
        <div>
          <h5 className="font-semibold text-content dark:text-white mb-2">Recommendations</h5>
          {previewData.recommendations.length > 0 ? (
            <div className="space-y-1">
              {previewData.recommendations.map((rec, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle className="w-3 h-3 text-emerald-500 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-content-muted dark:text-gray-400">{rec}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-content-subtle dark:text-gray-500">No recommendations</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced Segment Preview Component with AI insights and quality analysis
const EnhancedSegmentPreview = ({ 
  previewData, 
  isLoading, 
  totalLeads, 
  criteria, 
  leads, 
  isAiGenerated, 
  aiConfidence 
}) => {
  const [showSampleLeads, setShowSampleLeads] = useState(false);
  const [showQualityDetails, setShowQualityDetails] = useState(false);

  if (isLoading) {
    return (
      <div className="rounded-xl border border-line dark:border-white/[0.07] bg-surface dark:bg-white/[0.02] p-6">
        <div className="flex items-center gap-3">
          <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full flex-shrink-0"></div>
          <div>
            <h4 className="font-semibold text-content dark:text-white">Calculating segment preview...</h4>
            <p className="text-sm text-content-muted dark:text-gray-400">Analyzing your leads data</p>
          </div>
        </div>
      </div>
    );
  }

  if (!previewData) {
    return null;
  }

  const { estimatedSize, sampleProfiles, qualityScore, recommendations, percentage } = previewData;

  const getQualityColor = (score) => {
    if (score >= 80) return 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/20';
    if (score >= 60) return 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/20';
    return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20';
  };

  const getQualityLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Improvement';
  };

  const getSizeRecommendation = (size, total) => {
    const pct = total > 0 ? (size / total) * 100 : 0;
    if (size === 0) return { type: 'error', message: 'No matching leads found' };
    if (pct < 1) return { type: 'warning', message: 'Very small segment - consider broadening criteria' };
    if (pct > 80) return { type: 'warning', message: 'Very large segment - consider adding more specific criteria' };
    if (pct >= 5 && pct <= 30) return { type: 'success', message: 'Well-sized segment for targeted campaigns' };
    return { type: 'info', message: 'Moderate segment size' };
  };

  const sizeRec = getSizeRecommendation(estimatedSize, totalLeads);

  return (
    <div className="space-y-4">
      {/* Main Preview Card */}
      <div className="rounded-xl border border-line dark:border-white/[0.07] bg-surface dark:bg-white/[0.02] p-6">
        <div className="flex items-center justify-between mb-5">
          <h4 className="text-base font-bold text-content dark:text-white flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600 dark:text-indigo-400" />
            Segment Preview
          </h4>
          {isAiGenerated && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 dark:bg-purple-500/10 border border-purple-100 dark:border-purple-500/20">
              <Brain className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
              <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">AI Generated</span>
            </div>
          )}
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-xl p-4">
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-300">{estimatedSize.toLocaleString()}</div>
            <div className="text-sm font-medium text-indigo-700 dark:text-indigo-400">Matching Leads</div>
            <div className="text-xs text-blue-600 dark:text-blue-500 mt-1">
              {percentage}% of total ({totalLeads.toLocaleString()})
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-white/[0.02] border border-line dark:border-white/[0.07] rounded-xl p-4">
            <div className={`text-2xl font-bold ${
              sizeRec.type === 'success' ? 'text-emerald-700 dark:text-emerald-400' :
              sizeRec.type === 'warning' ? 'text-amber-700 dark:text-amber-400' :
              sizeRec.type === 'error' ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
            }`}>
              {sizeRec.type === 'success' ? '✓' : 
               sizeRec.type === 'warning' ? '⚠' :
               sizeRec.type === 'error' ? '✗' : 'ℹ'}
            </div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Size Quality</div>
            <div className="text-xs text-content-muted dark:text-gray-400 mt-1">{sizeRec.message}</div>
          </div>

          <div className="bg-purple-50 dark:bg-purple-500/10 border border-purple-100 dark:border-purple-500/20 rounded-xl p-4">
            <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
              {qualityScore}
            </div>
            <div className="text-sm font-medium text-purple-700 dark:text-purple-400">Quality Score</div>
            <div className="text-xs text-purple-600 dark:text-purple-500 mt-1">{getQualityLabel(qualityScore)}</div>
          </div>

          {isAiGenerated && (
            <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-xl p-4">
              <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">{aiConfidence}%</div>
              <div className="text-sm font-medium text-indigo-700 dark:text-indigo-400">AI Confidence</div>
              <div className="text-xs text-indigo-600 dark:text-indigo-500 mt-1">
                {aiConfidence >= 80 ? 'High' : aiConfidence >= 60 ? 'Medium' : 'Low'}
              </div>
            </div>
          )}
        </div>

        {/* Sample Leads Section */}
        {sampleProfiles && sampleProfiles.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-sm font-semibold text-content dark:text-white">Sample Matching Leads</h5>
              <button
                onClick={() => setShowSampleLeads(!showSampleLeads)}
                className="text-sm text-blue-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-blue-300 font-semibold transition-colors"
              >
                {showSampleLeads ? 'Hide' : 'Show'} Details
              </button>
            </div>
            
            {showSampleLeads ? (
              <div className="space-y-2">
                {sampleProfiles.slice(0, 3).map((lead, index) => (
                  <div key={lead.id || index} className="bg-gray-50 dark:bg-white/[0.03] rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 dark:bg-indigo-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                            {lead.name?.charAt(0) || 'L'}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-content dark:text-white">{lead.name}</div>
                          <div className="text-xs text-content-muted dark:text-gray-400">{lead.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          lead.status === 'hot' ? 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300' :
                          lead.status === 'warm' ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300' :
                          'bg-blue-100 dark:bg-indigo-500/20 text-blue-800 dark:text-blue-300'
                        }`}>
                          {lead.status}
                        </span>
                        <span className="text-xs text-content-muted dark:text-gray-400">Score: {lead.leadScore}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {sampleProfiles.length > 3 && (
                  <div className="text-center text-sm text-content-muted dark:text-gray-400 py-2">
                    +{sampleProfiles.length - 3} more leads match your criteria
                  </div>
                )}
              </div>
            ) : (
              <div className="flex -space-x-2">
                {sampleProfiles.slice(0, 5).map((lead, index) => (
                  <div
                    key={lead.id || index}
                    className="w-8 h-8 bg-blue-100 dark:bg-indigo-500/20 rounded-full flex items-center justify-center border-2 border-white dark:border-dark-800"
                    title={lead.name}
                  >
                    <span className="text-xs font-semibold text-blue-800 dark:text-blue-300">
                      {lead.name?.charAt(0) || 'L'}
                    </span>
                  </div>
                ))}
                {sampleProfiles.length > 5 && (
                  <div className="w-8 h-8 bg-gray-100 dark:bg-white/[0.08] rounded-full flex items-center justify-center border-2 border-white dark:border-dark-800">
                    <span className="text-xs font-semibold text-content-secondary dark:text-gray-300">
                      +{sampleProfiles.length - 5}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Recommendations */}
        {recommendations && recommendations.length > 0 && (
          <div className="mb-4">
            <h5 className="text-sm font-semibold text-content dark:text-white mb-2">Recommendations</h5>
            <div className="space-y-2">
              {recommendations.map((rec, index) => (
                <div key={index} className="flex items-start gap-2 text-sm">
                  <div className="w-4 h-4 rounded-full bg-blue-100 dark:bg-indigo-500/20 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <span className="text-xs">💡</span>
                  </div>
                  <span className="text-content-secondary dark:text-gray-300">{rec}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quality Analysis Toggle */}
        <div className="border-t border-line-subtle dark:border-white/[0.05] pt-4">
          <button
            onClick={() => setShowQualityDetails(!showQualityDetails)}
            className="flex items-center gap-2 text-sm font-semibold text-content-secondary dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            <span>Quality Analysis</span>
            <span className={`transform transition-transform ${showQualityDetails ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </button>
          
          {showQualityDetails && (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-xl p-3">
                  <div className="text-xs font-semibold text-blue-900 dark:text-blue-300 mb-1">Targeting Precision</div>
                  <div className="text-sm text-blue-800 dark:text-indigo-400">
                    {percentage < 5 ? 'Highly Targeted' :
                     percentage < 20 ? 'Well Targeted' :
                     percentage < 50 ? 'Moderately Targeted' : 'Broad Targeting'}
                  </div>
                </div>
                
                <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 rounded-xl p-3">
                  <div className="text-xs font-semibold text-emerald-900 dark:text-emerald-300 mb-1">Criteria Complexity</div>
                  <div className="text-sm text-emerald-800 dark:text-emerald-400">
                    {criteria.length >= 3 ? 'Complex' :
                     criteria.length >= 2 ? 'Moderate' : 'Simple'}
                  </div>
                </div>
                
                <div className="bg-purple-50 dark:bg-purple-500/10 border border-purple-100 dark:border-purple-500/20 rounded-xl p-3">
                  <div className="text-xs font-semibold text-purple-900 dark:text-purple-300 mb-1">Expected Performance</div>
                  <div className="text-sm text-purple-800 dark:text-purple-400">
                    {qualityScore >= 80 ? 'High' :
                     qualityScore >= 60 ? 'Medium' : 'Low'}
                  </div>
                </div>
              </div>
              
              {isAiGenerated && (
                <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-xl p-3">
                  <div className="text-xs font-semibold text-indigo-900 dark:text-indigo-300 mb-1">AI Analysis</div>
                  <div className="text-sm text-indigo-800 dark:text-indigo-400">
                    The AI interpreted your description with {aiConfidence}% confidence. 
                    {aiConfidence >= 80 ? ' The criteria should closely match your intent.' :
                     aiConfidence >= 60 ? ' The criteria are a good match but may need refinement.' :
                     ' Consider reviewing and adjusting the generated criteria.'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Performance Predictions */}
      {estimatedSize > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-indigo-500/10 dark:to-purple-500/10 border border-blue-200 dark:border-indigo-500/20 rounded-xl p-5">
          <h5 className="text-sm font-semibold text-content dark:text-white mb-3">Campaign Performance Predictions</h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-900 dark:text-blue-300">
                {Math.round(estimatedSize * 0.25)}
              </div>
              <div className="text-xs text-indigo-700 dark:text-indigo-400">Expected Opens (25%)</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-purple-900 dark:text-purple-300">
                {Math.round(estimatedSize * 0.05)}
              </div>
              <div className="text-xs text-purple-700 dark:text-purple-400">Expected Clicks (5%)</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-emerald-900 dark:text-emerald-300">
                {Math.round(estimatedSize * 0.02)}
              </div>
              <div className="text-xs text-emerald-700 dark:text-emerald-400">Expected Conversions (2%)</div>
            </div>
          </div>
          <div className="text-xs text-content-muted dark:text-gray-500 mt-2 text-center">
            * Estimates based on industry averages and segment quality
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateSegmentModal;